import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EnvironmentToggle = () => {
  const [currentEnvironment, setCurrentEnvironment] = useState('production');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingEnvironment, setPendingEnvironment] = useState(null);

  const environments = [
    {
      id: 'production',
      name: 'Production',
      description: 'Live environment with real data and active integrations',
      icon: 'Globe',
      color: 'text-success',
      bgColor: 'bg-success/10',
      status: 'active',
      lastDeployment: '2024-10-28T16:45:00Z',
      uptime: '99.97%',
      activeConnections: 847,
      dataVolume: '2.3 TB'
    },
    {
      id: 'sandbox',
      name: 'Sandbox',
      description: 'Safe testing environment with sample data',
      icon: 'TestTube',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      status: 'standby',
      lastDeployment: '2024-10-29T10:20:00Z',
      uptime: '99.85%',
      activeConnections: 23,
      dataVolume: '156 GB'
    },
    {
      id: 'development',
      name: 'Development',
      description: 'Development environment for testing new features',
      icon: 'Code',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      status: 'maintenance',
      lastDeployment: '2024-10-29T14:15:00Z',
      uptime: '98.42%',
      activeConnections: 5,
      dataVolume: '45 GB'
    }
  ];

  const handleEnvironmentSwitch = (envId) => {
    if (envId === currentEnvironment) return;
    
    setPendingEnvironment(envId);
    setShowConfirmDialog(true);
  };

  const confirmEnvironmentSwitch = () => {
    setCurrentEnvironment(pendingEnvironment);
    setShowConfirmDialog(false);
    setPendingEnvironment(null);
  };

  const cancelEnvironmentSwitch = () => {
    setShowConfirmDialog(false);
    setPendingEnvironment(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'standby': return 'Clock';
      case 'maintenance': return 'Settings';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'standby': return 'text-warning';
      case 'maintenance': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Environment Management</h3>
          <p className="text-sm text-text-secondary mt-1">
            Switch between production, sandbox, and development environments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-success rounded-full animate-pulse-soft"></div>
          <span className="text-sm font-medium text-foreground">
            Current: {environments?.find(env => env.id === currentEnvironment)?.name}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {environments?.map((env) => (
          <div 
            key={env.id}
            className={`bg-card border rounded-lg p-6 transition-all duration-200 cursor-pointer hover:shadow-md ${
              currentEnvironment === env.id 
                ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => handleEnvironmentSwitch(env.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg ${env.bgColor}`}>
                <Icon name={env.icon} size={24} className={env.color} />
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={getStatusIcon(env.status)} 
                  size={16} 
                  className={getStatusColor(env.status)}
                />
                <span className={`text-xs font-medium capitalize ${getStatusColor(env.status)}`}>
                  {env.status}
                </span>
              </div>
            </div>
            
            <h4 className="text-lg font-semibold text-card-foreground mb-2">{env.name}</h4>
            <p className="text-sm text-text-secondary mb-4">{env.description}</p>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">Uptime:</span>
                <span className="font-medium text-card-foreground">{env.uptime}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">Connections:</span>
                <span className="font-medium text-card-foreground">{env.activeConnections}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">Data Volume:</span>
                <span className="font-medium text-card-foreground">{env.dataVolume}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">Last Deploy:</span>
                <span className="font-medium text-card-foreground">
                  {new Date(env.lastDeployment)?.toLocaleDateString()}
                </span>
              </div>
            </div>
            
            {currentEnvironment === env.id && (
              <div className="mt-4 flex items-center gap-2 text-sm text-success">
                <Icon name="CheckCircle" size={16} />
                <span className="font-medium">Currently Active</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Environment Health Status */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-card-foreground mb-4">Environment Health Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Server" size={24} className="text-success" />
            </div>
            <h5 className="font-medium text-card-foreground mb-1">System Health</h5>
            <p className="text-2xl font-bold text-success mb-1">98.7%</p>
            <p className="text-sm text-text-secondary">All systems operational</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Activity" size={24} className="text-primary" />
            </div>
            <h5 className="font-medium text-card-foreground mb-1">API Response</h5>
            <p className="text-2xl font-bold text-primary mb-1">142ms</p>
            <p className="text-sm text-text-secondary">Average response time</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Database" size={24} className="text-warning" />
            </div>
            <h5 className="font-medium text-card-foreground mb-1">Data Sync</h5>
            <p className="text-2xl font-bold text-warning mb-1">2.1s</p>
            <p className="text-sm text-text-secondary">Last synchronization</p>
          </div>
        </div>
      </div>
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-300 animate-fade-in">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <h4 className="text-lg font-semibold text-card-foreground">Confirm Environment Switch</h4>
            </div>
            
            <p className="text-text-secondary mb-6">
              Are you sure you want to switch from{' '}
              <span className="font-medium text-card-foreground">
                {environments?.find(env => env.id === currentEnvironment)?.name}
              </span>{' '}
              to{' '}
              <span className="font-medium text-card-foreground">
                {environments?.find(env => env.id === pendingEnvironment)?.name}
              </span>
              ? This will affect all active connections and data processing.
            </p>
            
            <div className="flex items-center gap-3">
              <Button variant="default" onClick={confirmEnvironmentSwitch}>
                Confirm Switch
              </Button>
              <Button variant="outline" onClick={cancelEnvironmentSwitch}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentToggle;